import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { useJsApiLoader, GoogleMap, Marker, DirectionsRenderer } from '@react-google-maps/api';
import './RouteWithDetour.css';
import { geocodeAddress } from '../services/geocoding';

const ISRAEL_CENTER = { lat: 32.0853, lng: 34.7818 };
const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  language: 'he',
  region: 'il'
};

function RouteWithDetour({
  origin,
  destination,
  pickupLocation, // Single passenger's pickup location { address, lat, lng, name? } (for backward compatibility)
  pickupLocations, // Array of passenger pickup locations [{ address, lat, lng, name }, ...]
  passengerName, // Optional: Passenger name to display on marker (for single passenger)
  height = '400px'
}) {
  const [originalDirections, setOriginalDirections] = useState(null);
  const [detourDirections, setDetourDirections] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [detourInfo, setDetourInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passengerCount, setPassengerCount] = useState(0);
  const [passengerMarkers, setPassengerMarkers] = useState([]);
  const [mapCenter] = useState(ISRAEL_CENTER);
  const [mapZoom] = useState(10);
  const directionsServiceRef = useRef(null);
  const mapRef = useRef(null);

  const getLatLng = useCallback(async (location) => {
    if (!location) return null;
    
    // If already has lat/lng - validate and convert to numbers
    if (typeof location === 'object' && location.lat != null && location.lng != null) {
      const lat = typeof location.lat === 'string' ? parseFloat(location.lat) : location.lat;
      const lng = typeof location.lng === 'string' ? parseFloat(location.lng) : location.lng;
      
      // Validate that they are valid numbers
      if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    
    const address = typeof location === 'string' ? location : (location.address || location);
    if (!address || (typeof address === 'string' && address.trim() === '')) return null;

    try {
      const result = await geocodeAddress(address);
      if (result && typeof result.lat === 'number' && typeof result.lng === 'number') {
      return result;
      }
      return null;
    } catch (err) {
      console.error('Geocoding error:', err);
      return null;
    }
  }, []);

  const formatDuration = useCallback((minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    
    if (hours > 0) {
      return `${hours} שעות ${mins} דקות`;
    }
    return `${mins} דקות`;
  }, []);

  const [mapReady, setMapReady] = useState(false);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (window.google && window.google.maps) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
      setMapReady(true);
    }
  }, []);

  // Calculate routes
  useEffect(() => {
    if (!mapReady || !directionsServiceRef.current) return;

    const calculateRoutes = async () => {
      setIsLoading(true);
      setError(null);
      setOriginalDirections(null);
      setDetourDirections(null);
      setPassengerMarkers([]);

      try {
        // Get coordinates
        const originCoords = await getLatLng(origin);
        
        let destCoords = null;
        if (typeof destination === 'object' && destination.lat != null && destination.lng != null) {
          destCoords = { 
            lat: typeof destination.lat === 'string' ? parseFloat(destination.lat) : destination.lat, 
            lng: typeof destination.lng === 'string' ? parseFloat(destination.lng) : destination.lng, 
            address: destination.address || 'Event Location'
          };
        } else {
          destCoords = await getLatLng(destination);
        }

        // Validate coordinates are proper numbers
        const validateCoords = (coords) => {
          return coords && 
            typeof coords.lat === 'number' && 
            typeof coords.lng === 'number' && 
            !isNaN(coords.lat) && 
            !isNaN(coords.lng) &&
            coords.lat >= -90 && coords.lat <= 90 &&
            coords.lng >= -180 && coords.lng <= 180;
        };

        if (!originCoords || !destCoords || !validateCoords(originCoords) || !validateCoords(destCoords)) {
          let errorMsg = 'לא ניתן למצוא את המיקומים';
          if (!origin || !destination) {
            errorMsg = 'חסרים פרטי מיקום';
          } else {
            const missingOrigin = (!originCoords || !validateCoords(originCoords)) ? `מיקום מוצא: ${typeof origin === 'string' ? origin : origin?.address || 'לא הוגדר'}` : '';
            const missingDest = (!destCoords || !validateCoords(destCoords)) ? `מיקום יעד: ${typeof destination === 'string' ? destination : destination?.address || 'לא הוגדר'}` : '';
            errorMsg = `לא ניתן למצוא את המיקומים. ${missingOrigin} ${missingDest}`.trim();
          }
          console.error('Invalid coordinates:', { origin: originCoords, destination: destCoords });
          setError(errorMsg);
          setIsLoading(false);
          return;
        }

        // Collect all passenger pickup locations
        const allPassengers = [];
        
        if (pickupLocations && Array.isArray(pickupLocations) && pickupLocations.length > 0) {
          // Multiple passengers mode
          for (const pickup of pickupLocations) {
            const coords = await getLatLng(pickup);
            if (coords && coords.lat != null && coords.lng != null) {
              const lat = typeof coords.lat === 'number' ? coords.lat : parseFloat(coords.lat);
              const lng = typeof coords.lng === 'number' ? coords.lng : parseFloat(coords.lng);
              
              // Only add if coordinates are valid numbers
              if (!isNaN(lat) && !isNaN(lng)) {
              allPassengers.push({
                  coords: { lat, lng },
                name: pickup.name || passengerName || 'נוסע',
                address: pickup.address || pickupLocation?.address || 'מיקום איסוף'
              });
              }
            }
          }
        } else if (pickupLocation) {
          // Single passenger mode (backward compatibility)
          let pickupCoords;
          if (typeof pickupLocation === 'object' && pickupLocation.lat != null && pickupLocation.lng != null) {
            const lat = typeof pickupLocation.lat === 'string' ? parseFloat(pickupLocation.lat) : pickupLocation.lat;
            const lng = typeof pickupLocation.lng === 'string' ? parseFloat(pickupLocation.lng) : pickupLocation.lng;
            
            // Only use if coordinates are valid numbers
            if (!isNaN(lat) && !isNaN(lng)) {
              pickupCoords = { lat, lng };
            } else {
              // If invalid, try to geocode the address
              pickupCoords = await getLatLng(pickupLocation);
            }
          } else {
            pickupCoords = await getLatLng(pickupLocation);
          }
          if (pickupCoords && pickupCoords.lat != null && pickupCoords.lng != null) {
            const lat = typeof pickupCoords.lat === 'number' ? pickupCoords.lat : parseFloat(pickupCoords.lat);
            const lng = typeof pickupCoords.lng === 'number' ? pickupCoords.lng : parseFloat(pickupCoords.lng);
            
            if (!isNaN(lat) && !isNaN(lng)) {
            allPassengers.push({
                coords: { lat, lng },
              name: passengerName || pickupLocation?.name || 'נוסע',
              address: pickupLocation?.address || 'מיקום איסוף'
            });
            }
          }
        }

        setPassengerCount(allPassengers.length);

        // Calculate original route (no pickups) - use Number() to guarantee type
        directionsServiceRef.current.route(
          {
            origin: { lat: Number(originCoords.lat), lng: Number(originCoords.lng) },
            destination: { lat: Number(destCoords.lat), lng: Number(destCoords.lng) },
            travelMode: window.google.maps.TravelMode.DRIVING,
            language: 'he',
            region: 'il'
          },
          (result, status) => {
            if (status === 'OK' && result) {
              setOriginalDirections(result);
              
              // Extract route info
              let totalDistance = 0;
              let totalDuration = 0;

              result.routes[0].legs.forEach(leg => {
                totalDistance += leg.distance.value; // meters
                totalDuration += leg.duration.value; // seconds
              });

              setRouteInfo({
                distance: (totalDistance / 1000).toFixed(1),
                duration: totalDuration / 60 // minutes
              });
            }
          }
        );

        // Calculate route with all passengers
        if (allPassengers.length > 0) {
          const waypoints = allPassengers
            .filter(p => p.coords && typeof p.coords.lat === 'number' && typeof p.coords.lng === 'number' && !isNaN(p.coords.lat) && !isNaN(p.coords.lng))
            .map(p => ({
              location: { lat: Number(p.coords.lat), lng: Number(p.coords.lng) },
            stopover: true
          }));

          directionsServiceRef.current.route(
            {
              origin: { lat: Number(originCoords.lat), lng: Number(originCoords.lng) },
              destination: { lat: Number(destCoords.lat), lng: Number(destCoords.lng) },
              waypoints: waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
              language: 'he',
              region: 'il',
              optimizeWaypoints: false
            },
            (result, status) => {
              if (status === 'OK' && result) {
                setDetourDirections(result);
                
                // Extract route info
                let totalDistance = 0;
                let totalDuration = 0;

                result.routes[0].legs.forEach(leg => {
                  totalDistance += leg.distance.value; // meters
                  totalDuration += leg.duration.value; // seconds
                });

                const originalDistance = routeInfo ? parseFloat(routeInfo.distance) : 0;
                const originalDuration = routeInfo ? routeInfo.duration : 0;
                const additionalDistance = (totalDistance / 1000) - originalDistance;
                const additionalDuration = (totalDuration / 60) - originalDuration;

                setDetourInfo({
                  distance: (totalDistance / 1000).toFixed(1),
                  duration: totalDuration / 60,
                  additionalDistance: additionalDistance.toFixed(1),
                  additionalDuration: additionalDuration
                });

                // Set passenger markers
                setPassengerMarkers(allPassengers);

                // Fit bounds to show all points
                if (mapRef.current && result.routes[0].bounds) {
                  mapRef.current.fitBounds(result.routes[0].bounds);
                }
              }
              
              setIsLoading(false);
            }
          );
        } else {
          setIsLoading(false);
        }

      } catch (err) {
        console.error('Route calculation error:', err);
        setError('שגיאה בחישוב המסלול');
        setIsLoading(false);
      }
    };

    calculateRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, destination, pickupLocation, pickupLocations, passengerName, mapReady]);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  const { isLoaded: isGoogleLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: 'he',
    region: 'IL'
  });

  if (!apiKey) {
    return (
      <div className="route-with-detour-container">
        <div className="route-with-detour-error">
          ⚠️ מפתח Google Maps API לא הוגדר
        </div>
      </div>
    );
  }

  if (!isGoogleLoaded) {
    return (
      <div className="route-with-detour-container">
        <div className="map-loading">
          <div className="map-spinner"></div>
          <span>טוען מפה...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="route-with-detour-container">
        <div className="route-with-detour-error">
          ⚠️ שגיאה בטעינת המפה
        </div>
      </div>
    );
  }

  // Get coordinates for origin and destination markers
  const originCoords = origin && typeof origin === 'object' && origin.lat != null && origin.lng != null
    ? { lat: origin.lat, lng: origin.lng }
    : null;
  
  const destCoords = destination && typeof destination === 'object' && destination.lat != null && destination.lng != null
    ? { lat: destination.lat, lng: destination.lng }
    : null;

  return (
    <div className="route-with-detour-container">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height }}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad}
          options={mapOptions}
        >
          {/* Original route (dashed line) */}
          {originalDirections && (
            <DirectionsRenderer
              directions={originalDirections}
              options={{
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: '#3b82f6',
                  strokeWeight: 6,
                  strokeOpacity: 0.7,
                  icons: [{
                    icon: {
                      path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW,
                      scale: 3,
                      strokeColor: '#3b82f6'
                    },
                    offset: '50%',
                    repeat: '100px'
                  }]
                }
              }}
            />
          )}
          
          {/* Detour route (solid bold line) */}
          {detourDirections && (
            <DirectionsRenderer
              directions={detourDirections}
              options={{
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: '#f59e0b',
                  strokeWeight: 7,
                  strokeOpacity: 0.9
                }
              }}
            />
          )}
          
          {/* Origin marker (Driver) */}
          {originCoords && typeof originCoords.lat === 'number' && typeof originCoords.lng === 'number' && !isNaN(originCoords.lat) && !isNaN(originCoords.lng) && (
            <Marker
              position={originCoords}
              label={{ text: '🚗', color: 'white', fontSize: '16px' }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                scale: 16,
                fillColor: '#22c55e',
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 3
              }}
            />
          )}
          
          {/* Destination marker (Event) */}
          {destCoords && typeof destCoords.lat === 'number' && typeof destCoords.lng === 'number' && !isNaN(destCoords.lat) && !isNaN(destCoords.lng) && (
            <Marker
              position={destCoords}
              label={{ text: '🎯', color: 'white', fontSize: '16px' }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                scale: 16,
                fillColor: '#ef4444',
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 3
              }}
            />
          )}
          
          {/* Passenger markers */}
          {passengerMarkers.map((passenger, idx) => {
            const lat = typeof passenger.coords.lat === 'number' ? passenger.coords.lat : parseFloat(passenger.coords.lat);
            const lng = typeof passenger.coords.lng === 'number' ? passenger.coords.lng : parseFloat(passenger.coords.lng);
            
            // Only render marker if coordinates are valid numbers
            if (isNaN(lat) || isNaN(lng)) {
              return null;
            }
            
            return (
            <Marker
              key={idx}
                position={{ lat, lng }}
              label={{ text: '👤', color: 'white', fontSize: '14px' }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                scale: 14,
                fillColor: '#f59e0b',
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 3
              }}
            />
            );
          })}
        </GoogleMap>

      {isLoading && (
        <div className="map-loading">
          <div className="map-spinner"></div>
          <span>טוען מפה...</span>
        </div>
      )}

      {error && (
        <div className="route-with-detour-error">
          ⚠️ {error}
        </div>
      )}

      {(routeInfo || detourInfo) && (
        <div className="route-with-detour-info">
          <div className="route-comparison-header">
            <h4>📊 השוואת מסלולים</h4>
          </div>
          
          {routeInfo && (
            <div className="route-original">
              <div className="route-label">
                <span className="route-label-icon">🔵</span>
                <h4>לפני נוסעים (מסלול מקורי)</h4>
              </div>
              <div className="route-stats">
                <div className="route-stat">
                  <span className="stat-icon">🚗</span>
                  <span className="stat-value">{routeInfo.distance} ק"מ</span>
                </div>
                <div className="route-stat">
                  <span className="stat-icon">⏱️</span>
                  <span className="stat-value">{formatDuration(routeInfo.duration)}</span>
                </div>
              </div>
            </div>
          )}

          {detourInfo && (
            <div className="route-detour">
              <div className="route-label">
                <span className="route-label-icon">🟠</span>
                <h4>אחרי נוסעים (מסלול עם סטייה)</h4>
              </div>
              <div className="route-stats">
                <div className="route-stat">
                  <span className="stat-icon">🚗</span>
                  <span className="stat-value">{detourInfo.distance} ק"מ</span>
                </div>
                <div className="route-stat">
                  <span className="stat-icon">⏱️</span>
                  <span className="stat-value">{formatDuration(detourInfo.duration)}</span>
                </div>
              </div>
              
              {detourInfo.additionalDistance && parseFloat(detourInfo.additionalDistance) > 0 && (
                <div className="detour-additional">
                  <div className="additional-header">
                    <span className="additional-icon">➕</span>
                    <strong>תוספת בגלל {passengerCount > 1 ? 'הנוסעים' : 'הנוסע'}:</strong>
                  </div>
                  <div className="additional-stats">
                    <div className="additional-stat">
                      <span className="additional-label">מרחק נוסף:</span>
                      <span className="additional-value highlight">+{detourInfo.additionalDistance} ק"מ</span>
                    </div>
                    <div className="additional-stat">
                      <span className="additional-label">זמן נוסף:</span>
                      <span className="additional-value highlight">+{formatDuration(detourInfo.additionalDuration)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Memoize component to prevent unnecessary re-renders
export default memo(RouteWithDetour, (prevProps, nextProps) => {
  // Only re-render if these critical props change
  return (
    prevProps.origin === nextProps.origin &&
    JSON.stringify(prevProps.destination) === JSON.stringify(nextProps.destination) &&
    JSON.stringify(prevProps.pickupLocation) === JSON.stringify(nextProps.pickupLocation) &&
    JSON.stringify(prevProps.pickupLocations) === JSON.stringify(nextProps.pickupLocations) &&
    prevProps.passengerName === nextProps.passengerName &&
    prevProps.height === nextProps.height
  );
});
